import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  TextOutlinedInput,
  useMobile,
} from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { graduateListFilter } from '../../../api/requests/graduationProcessMassive';

const dictPrefix = 'graduatesList.searchControls';

const SEARCH_TYPE_OPTIONS: OutlinedSelectOptionType[] = [
  { label: 'ID', value: 'identificator' },
  { label: 'Nombre', value: 'name' },
];

const DEFAULT_VALUES = {
  search: '',
  searchBy: null,
  cohortPeriodId: null,
  schoolId: null,
  studyPlanId: null,
  campusId: null,
  graduationPeriodId: null,
};

interface FormValues {
  search: string;
  searchBy: OutlinedSelectOptionType | null;
  cohortPeriodId: OutlinedSelectOptionType | null;
  schoolId: OutlinedSelectOptionType | null;
  studyPlanId: OutlinedSelectOptionType | null;
  campusId: OutlinedSelectOptionType | null;
  graduationPeriodId: OutlinedSelectOptionType | null;
}

export type SearchParams = {
  search?: string;
  searchBy?: string | number;
  cohortPeriodId?: number;
  schoolId?: number;
  studyPlanId?: number;
  campusId?: number;
  graduationPeriodId?: number;
  hasFilters?: boolean;
};

interface Props {
  onSearch?: (sp: SearchParams) => void;
  onClear?: () => void;
  onDownload?: () => void;
  downloadIsAvailable?: boolean;
  isDownloadLoading?: boolean;
}

export function SearchControls({
  onSearch = () => null,
  onClear = () => null,
  onDownload = () => null,
  downloadIsAvailable = true,
  isDownloadLoading = false,
}: Props) {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const [periods, setPeriods] = useState<OutlinedSelectOptionType[]>([]);
  const [schools, setSchools] = useState<OutlinedSelectOptionType[]>([]);
  const [studyPlans, setStudyPlans] = useState<OutlinedSelectOptionType[]>([]);
  const [campuses, setCampuses] = useState<OutlinedSelectOptionType[]>([]);

  const { handleSubmit, control, reset, watch } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const [searchBy, search, campusIdWatch] = watch([
    'searchBy',
    'search',
    'campusId',
  ]);

  const handleSearch = (values: FormValues) => {
    const {
      search,
      searchBy,
      cohortPeriodId,
      schoolId,
      studyPlanId,
      graduationPeriodId,
    } = values;

    onSearch({
      search: search?.trim() !== '' ? search : undefined,
      searchBy: searchBy?.value,
      cohortPeriodId: cohortPeriodId?.value as number,
      schoolId: schoolId?.value as number,
      studyPlanId: studyPlanId?.value as number,
      campusId: campusIdWatch?.value as number,
      graduationPeriodId: graduationPeriodId?.value as number,
      hasFilters: Object.values(values).some((value) => value !== null),
    });
  };

  const handleClear = () => {
    reset(DEFAULT_VALUES);
    onClear();
  };

  const getFilters = useCallback(async () => {
    const { data } = await graduateListFilter();
    if (data) {
      setPeriods(
        data.data.periods.map(({ id: value, name: label }) => ({
          label,
          value,
        })),
      );
      setSchools(
        data.data.schools.map(({ id: value, name: label }) => ({
          label,
          value,
        })),
      );
      setStudyPlans(
        data.data.studyPlans.map(({ id: value, name: label }) => ({
          label,
          value,
        })),
      );
      setCampuses(
        data.data.campuses.map(({ id: value, name: label }) => ({
          label,
          value,
        })),
      );
      reset();
    }
  }, [reset]);

  useEffect(() => {
    getFilters();
  }, [getFilters]);

  return (
    <form onSubmit={handleSubmit(handleSearch)}>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="searchBy"
            label={t(`${dictPrefix}.searchBy`)}
            placeholder={t(`${dictPrefix}.searchByPlaceholder`)}
            control={control}
            options={SEARCH_TYPE_OPTIONS}
            rules={{
              validate: {
                validateSearchField: (value) => {
                  if (!value && search) {
                    return `${t(`${dictPrefix}.error`)}`;
                  } else {
                    return undefined;
                  }
                },
              },
            }}
          />
        </Col>

        <Col xs={12} md={6} lg={8}>
          <TextOutlinedInput
            name="search"
            label={'.'}
            control={control}
            placeholder={t(`${dictPrefix}.nameOrId`)}
            rules={{
              validate: {
                validateSearchByField: (value) => {
                  if (!value && searchBy) {
                    return `${t(`${dictPrefix}.error`)}`;
                  } else {
                    return undefined;
                  }
                },
              },
            }}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="cohortPeriodId"
            label={t(`${dictPrefix}.cohorte`)}
            placeholder={t(`${dictPrefix}.cohortPeriodPlaceholder`)}
            control={control}
            options={periods}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="schoolId"
            label={t(`${dictPrefix}.school`)}
            placeholder={t(`${dictPrefix}.schoolPlaceholder`)}
            control={control}
            options={schools}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="studyPlanId"
            label={t(`${dictPrefix}.studyPlan`)}
            placeholder={t(`${dictPrefix}.studyPlanPlaceholder`)}
            control={control}
            options={studyPlans}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="campusId"
            label={t(`${dictPrefix}.campus`)}
            placeholder={t(`${dictPrefix}.campusPlaceholder`)}
            control={control}
            options={campuses}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="graduationPeriodId"
            label={t(`${dictPrefix}.graduationPeriod`)}
            placeholder={t(`${dictPrefix}.graduationPeriodPlaceholder`)}
            control={control}
            options={periods}
          />
        </Col>

        <Col
          xs={12}
          md={{ size: 3, offset: 0 }}
          lg={{ size: 2, offset: 0 }}
          style={{ marginTop: isMobile ? 0 : '1.5rem' }}
        >
          <Button
            type="submit"
            text={t(`common.actions.search`)}
            size="md"
            fullwidth
          />
        </Col>
        <Col
          xs={12}
          md={{ size: 3 }}
          lg={2}
          style={{ marginTop: isMobile ? '1rem' : '1.5rem' }}
        >
          <Button
            text={t(`common.actions.clean`)}
            size="md"
            outlined
            fullwidth
            onClick={handleClear}
          />
        </Col>

        {downloadIsAvailable && (
          <Col
            xs={12}
            md={{ size: 6, offset: 6 }}
            lg={{ size: 4, offset: 8 }}
            style={{ marginTop: isMobile ? '1rem' : '1.5rem' }}
          >
            <Button
              className="ml-auto"
              size="md"
              fullwidth
              text={t(`common.actions.downloadList`)}
              icon="download"
              outlined
              loading={isDownloadLoading}
              onClick={onDownload}
            />
          </Col>
        )}
      </Row>
    </form>
  );
}
